import { InjectionToken } from '@angular/core';
import { Nexus } from '@tomandco/nexus-api-types';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib {
        namespace Catalog {
            namespace Model.Product.Data {
                interface V3 {
                    am_open_amount_min?: number;
                    am_open_amount_max?: number;
                    am_giftcard_prices?: number[];
                    am_giftcard_type?: number;
                    am_giftcard_code_image?: number[];
                }
            }

            namespace Model.Category.Data {
                interface V3 {
                    pageTemplate?: string;
                }
            }
        }

        namespace Basket {
            interface BasketData {
                giftcards?: Giftcards.IAddedGiftcardData[];
            }

            interface Totals {
                giftcards?: number;
            }
            interface AddToBasketData extends Giftcards.ISelectionContainer {}

            interface BuyRequest extends Giftcards.ISelectionContainer {}
        }
    }
}

export namespace Giftcards {
    export const giftcardTypeId = 'amgiftcard';

    export enum Type {
        Virtual = 1,
        Printed,
        Combined
    }

    export enum VirtualType {
        SendToMe = 'send_to_me',
        SendToAnother = 'send_to_another'
    }

    export enum Endpoints {
        Balance = 'giftcard/balance',
        Add = 'giftcard/add',
        Remove = 'giftcard/remove',
        CheckoutAdd = 'checkout/giftcardAdd',
        CheckoutRemove = 'checkout/giftcardRemove'
    }

    interface IAmountCustomPart {
        am_giftcard_amount_custom: number;
    }

    interface IAmountDefinedPart {
        am_giftcard_amount: number;
    }

    interface IRecipentSelfPart {
        is_send_to_myself: 1;
    }

    interface IRecipentDefinedPart {
        am_giftcard_recipient_email: string;
        am_giftcard_recipient_name?: string;
        // am_giftcard_sender_email: string; // provided later on checkout page
        am_giftcard_sender_name?: string;
        am_giftcard_message?: string;
        is_send_to_myself: 0;
    }

    interface ICommonPart {
        am_giftcard_date_delivery?: string;
    }

    export interface IInjectedPart {
        am_giftcard_image?: number;
    }

    type IAmountPart = IAmountCustomPart | IAmountDefinedPart;
    type IRecipentPart = IRecipentSelfPart | IRecipentDefinedPart;
    export type IRequest = IAmountPart & IRecipentPart & ICommonPart & IInjectedPart;
    export interface ISelectionContainer
        extends Partial<IAmountCustomPart>,
            Partial<IAmountDefinedPart>,
            Partial<Omit<IRecipentSelfPart, 'is_send_to_myself'>>,
            Partial<Omit<IRecipentDefinedPart, 'is_send_to_myself'>>,
            Partial<ICommonPart> {
        is_send_to_myself?: 1 | 0;
        recipientType?: ERecipientType;
        deliveryDateType?: EDeliveryDateType;
        am_giftcard_image?: number;
    }
    export interface ISelectionMeta {
        minAmount: number;
        maxAmount: number;
        amounts: number[];
        am_giftcard_code_image?: number;
    }

    export enum ERecipientType {
        myself,
        recipient
    }

    export enum EDeliveryDateType {
        immediate,
        date
    }

    export enum EValidationError {
        NotAllowed,
        NoAmount,
        NoRecipientData,
        NoSenderData,
        NoDeliveryDate,
        InvalidAmount
    }

    export interface IAddedGiftcardData {
        code: string;
        balance: number;
        amount: number;
        balance_after_purchase: number;
    }

    export interface IBalanceCheckData {
        code: string;
        pin?: string;
    }

    export interface IBalanceCheckResponse {
        balance: number;
    }

    export interface IAddData {
        code: string;
        checkout?: boolean;
    }

    export interface IRemoveData extends IAddData {}

    export const OPTIONS = new InjectionToken<IModuleOptions>('DF_MODULE_GTM_OPTIONS');

    export interface IModuleOptions {
        errors: Record<EValidationError, string>;
        defaultDeliveryDate: 'today' | 'tomorrow';
    }

    export const DEFAULT_OPTIONS: IModuleOptions = {
        errors: {
            [EValidationError.NotAllowed]: 'Product does not support giftcards configuration',
            [EValidationError.NoAmount]: 'Please provide amount for giftcard',
            [EValidationError.NoRecipientData]: 'Please provide recipient data for giftcard',
            [EValidationError.NoSenderData]: 'Please provide sender data for giftcard',
            [EValidationError.NoDeliveryDate]: 'Please provide delivery date for giftcard',
            [EValidationError.InvalidAmount]: 'Giftcard amount is invalid'
        },
        defaultDeliveryDate: 'today'
    };
}
